import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import type { GetSnapshots, LoadingType, PaginationData, PaginationInfo, Snapshot } from './types';

const initialState = {
    automaticUpdate: false,
    automaticUpload: false,
    automaticBulkActions: false,
    loading: 'getSnapshots' as LoadingType,
    settingLoading: false,
    paginationInfo: {
        limit: 25,
        offset: 0,
        total: 0,
    } as PaginationInfo,
    snapshots: [] as Snapshot[],
};

const projectSnapshotsSlice = createSlice({
    name: 'projectSnapshots',
    initialState,
    reducers: {
        setSnapshotsInfo: (state, { payload: { limit, offset, total, content } }: PayloadAction<GetSnapshots>) => {
            state.snapshots = content;
            state.paginationInfo = { limit, offset, total };
        },
        setSnapshotsLoading: (state, { payload }: PayloadAction<LoadingType>) => {
            state.loading = payload;
        },
        updateAutomaticUpload: (state, { payload }: PayloadAction<boolean>) => {
            state.automaticUpload = payload;
        },
        updateAutomaticUpdate: (state, { payload }: PayloadAction<boolean>) => {
            state.automaticUpdate = payload;
        },
        updateAutomaticBulkActions: (state, { payload }: PayloadAction<boolean>) => {
            state.automaticBulkActions = payload;
        },
        setSnapshotUpdate: (
            state,
            { payload: { index, description } }: PayloadAction<{ description: string; index: number }>,
        ) => {
            state.snapshots[index].title = description;
        },
    },
});

const getSnapshots = createAction(
    'projectSnapshots/getSnapshots',
    (projectId: string, paginationData: PaginationData) => ({ payload: { projectId, paginationData } }),
);

const setAutomaticUpload = createAction(
    'projectSnapshots/setAutomaticUpload',
    (enabled: boolean, projectId: string) => ({ payload: { enabled, projectId } }),
);

const setAutomaticUpdate = createAction(
    'projectSnapshots/setAutomaticUpdate',
    (enabled: boolean, projectId: string) => ({ payload: { enabled, projectId } }),
);

const setAutomaticBulkActions = createAction(
    'projectSnapshots/setAutomaticBulkActions',
    (enabled: boolean, projectId: string) => ({ payload: { enabled, projectId } }),
);

const updateSnapshot = createAction(
    'projectSnapshots/updateSnapshot',
    (snapshotCreatedAt: number, projectId: string, description: string, index: number) => ({
        payload: {
            snapshotCreatedAt,
            projectId,
            description,
            index,
        },
    }),
);

const restoreSnapshot = createAction(
    'projectSnapshots/restoreSnapshot',
    (projectId: string, snapshotCreatedAt: number) => ({ payload: { projectId, snapshotCreatedAt } }),
);

const deleteSnapshot = createAction(
    'projectSnapshots/deleteSnapshot',
    (projectId: string, snapshotCreatedAt: number, paginationData: PaginationData) => ({
        payload: {
            projectId,
            snapshotCreatedAt,
            paginationData,
        },
    }),
);

const deleteSnapshots = createAction(
    'projectSnapshots/deleteSnapshots',
    (projectId: string, snapshotsCreatedAt: number[], paginationData: PaginationData) => ({
        payload: {
            snapshotsCreatedAt,
            projectId,
            paginationData,
        },
    }),
);

const createSnapshot = createAction(
    'projectSnapshots/createSnapshot',
    (projectId: string, paginationData: PaginationData) => ({ payload: { projectId, paginationData } }),
);

export default projectSnapshotsSlice.reducer;

const {
    setSnapshotsInfo,
    setSnapshotsLoading,
    updateAutomaticUpload,
    updateAutomaticUpdate,
    setSnapshotUpdate,
    updateAutomaticBulkActions,
} = projectSnapshotsSlice.actions;

export const projectSnapshotsActions = {
    setSnapshotsInfo,
    setSnapshotsLoading,
    updateAutomaticUpload,
    updateAutomaticUpdate,
    updateAutomaticBulkActions,
    setSnapshotUpdate,
    getSnapshots,
    setAutomaticUpload,
    setAutomaticUpdate,
    setAutomaticBulkActions,
    updateSnapshot,
    restoreSnapshot,
    deleteSnapshot,
    deleteSnapshots,
    createSnapshot,
};
